import { useEffect, useRef } from "react";
import * as d3 from "d3";
import { apiFetch } from "../../api/apiClient";
import { normalizeProductionComparisonRows } from "../../util/productionComparison";

const HEIGHT = 520;

const margin = {
  top: 20,
  right: 20,
  bottom: 110,
  left: 90,
};

export default function CumulativeActualsVsPredicted({ portfolioName, data = [], onTooltip }) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const dataRef = useRef(null);
  const onTooltipRef = useRef(onTooltip);
  onTooltipRef.current = onTooltip;

  // Keyboard navigation: which x-axis point is selected and whether arrow-key
  // driven navigation is currently active. Refs (not state) so the d3 handlers
  // can read/write without forcing React re-renders or redraws.
  const keyboardIndexRef = useRef(0);
  const keyboardModeRef = useRef(false);

  useEffect(() => {
    if (!containerRef.current) return;

    // Tooltip div fallback (only when page-level ChartTooltip isn't used)
    const tooltip = onTooltipRef.current
      ? null
      : d3
          .select("body")
          .append("div")
          .style("position", "absolute")
          .style("left", "0")
          .style("top", "0")
          .style("background", "white")
          .style("border", "1px solid #ccc")
          .style("padding", "6px")
          .style("border-radius", "4px")
          .style("pointer-events", "none")
          .style("box-shadow", "0 0 10px rgba(0,0,0,0.25)")
          .style("opacity", 0);

    const formatNumber = d3.format(",.0f");
    const formatMwh = value => value == null ? '—' : `${formatNumber(value)} MWh`;

    // Mutable handle to the latest draw cycle's selection helpers. Populated
    // at the end of every `draw()` call so the effect-scoped keyboard/focus
    // listeners (which outlive a single draw) can drive the most recent
    // chart instance.
    const handlersRef = { current: null };

    function draw() {
      if (!containerRef.current || !dataRef.current?.length) return;

      const width = containerRef.current.clientWidth;
      const innerWidth = width - margin.left - margin.right;
      const innerHeight = HEIGHT - margin.top - margin.bottom;

      const svg = d3.select(svgRef.current);
      svg.selectAll("*").remove();

      const g = svg
        .append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);

      /* =========================
         SCALES
         ========================= */
      const x = d3
        .scaleBand()
        .domain(dataRef.current.map(d => d.date))
        .range([0, innerWidth])
        .padding(0.1);

      const maxVal = d3.max(
        dataRef.current,
        d => Math.max(d.actual ?? 0, d.predicted ?? 0),
      ) ?? 0;
      const yMax = Math.max(Math.ceil((maxVal * 1.1) / 1000) * 1000, 1000);
      const yTicks = d3.ticks(0, yMax, 6);

      const y = d3
        .scaleLinear()
        .domain([0, yMax])
        .range([innerHeight, 0]);

      /* =========================
         GRIDLINES
         ========================= */
      g.append("g")
        .call(
          d3.axisLeft(y)
            .tickValues(yTicks)
            .tickSize(-innerWidth)
            .tickFormat("")
        )
        .selectAll("line")
        .attr("stroke", "#d9d9d9");

      g.selectAll("path").remove();

      /* =========================
         AXES
         ========================= */
      g.append("g")
        .call(
          d3.axisLeft(y)
            .tickValues(yTicks)
            .tickFormat(d3.format(","))
        );

      g.append("g")
        .attr("transform", `translate(0,${innerHeight})`)
        .call(d3.axisBottom(x))
        .selectAll("text")
        .attr("transform", "rotate(45)")
        .attr("text-anchor", "start")
        .attr("dx", "0.6em")
        .attr("dy", "0.4em");

      /* =========================
         AREA GENERATORS
         ========================= */
      const areaActual = d3
        .area()
        .x(d => x(d.date) + x.bandwidth() / 2)
        .y0(y(0))
        .y1(d => y(d.actual));

      const areaPredicted = d3
        .area()
        .defined(d => d.predicted != null)
        .x(d => x(d.date) + x.bandwidth() / 2)
        .y0(y(0))
        .y1(d => y(d.predicted));

      /* =========================
         AREAS
         ========================= */
      // ACTUAL area (blue)
      g.append("path")
        .datum(dataRef.current)
        .attr("fill", "#4472C4")
        .attr("fill-opacity", 0.2)
        .attr("d", areaActual);

      // PREDICTED area (orange)
      g.append("path")
        .datum(dataRef.current)
        .attr("fill", "#ED7D31")
        .attr("fill-opacity", 0.2)
        .attr("d", areaPredicted);

      /* =========================
         LINE GENERATORS
         ========================= */
      const lineActual = d3
        .line()
        .x(d => x(d.date) + x.bandwidth() / 2)
        .y(d => y(d.actual));

      const linePredicted = d3
        .line()
        .defined(d => d.predicted != null)
        .x(d => x(d.date) + x.bandwidth() / 2)
        .y(d => y(d.predicted));

      /* =========================
         LINES
         ========================= */
      g.append("path")
        .datum(dataRef.current)
        .attr("fill", "none")
        .attr("stroke", "#4472C4")
        .attr("stroke-width", 3)
        .attr("d", lineActual);

      g.append("path")
        .datum(dataRef.current)
        .attr("fill", "none")
        .attr("stroke", "#ED7D31")
        .attr("stroke-width", 3)
        .attr("d", linePredicted);

      /* =========================
         DOTS — ACTUAL & PREDICTED
         (small by default; enlarged at the active crosshair index)
         ========================= */
      const DOT_R = 3;
      const DOT_R_ACTIVE = 6;

      const actualDots = g.selectAll(".dot-actual")
        .data(dataRef.current)
        .enter()
        .append("circle")
        .attr("class", "dot-actual")
        .attr("cx", d => x(d.date) + x.bandwidth() / 2)
        .attr("cy", d => y(d.actual))
        .attr("r", DOT_R)
        .attr("fill", "#4472C4")
        .style("pointer-events", "none");

      const predictedDots = g.selectAll(".dot-predicted")
        .data(dataRef.current.filter(d => d.predicted != null))
        .enter()
        .append("circle")
        .attr("class", "dot-predicted")
        .attr("cx", d => x(d.date) + x.bandwidth() / 2)
        .attr("cy", d => y(d.predicted))
        .attr("r", DOT_R)
        .attr("fill", "#ED7D31")
        .style("pointer-events", "none");

      /* =========================
         CROSSHAIR + TOOLTIP
         Snap-by-x to nearest x-axis point; tooltip anchors to the higher of
         the two line points (the smaller y value) at that date rather than
         following the cursor, for stable hover behavior.
         ========================= */
      const gCrosshair = g.append("g").attr("class", "crosshair");
      const gOverlay = g.append("g").attr("class", "overlay");

      const crosshair = gCrosshair.append("line")
        .attr("stroke", "#666")
        .attr("y1", 0)
        .attr("y2", innerHeight)
        .style("opacity", 0)
        .style("pointer-events", "none");

      const centers = dataRef.current.map(d => x(d.date) + x.bandwidth() / 2);

      const resetDots = () => {
        actualDots.attr("r", DOT_R);
        predictedDots.attr("r", DOT_R);
      };

      // Anchor the tooltip to the higher of the two line points (the smaller
      // y value) at the snapped date, centered on the x-axis point. The
      // fallback <div> tooltip uses page coordinates; the page-level callback
      // uses viewport coordinates so it can be positioned by a fixed-position
      // element.
      const showTooltipForDatum = (d, centerX) => {
        const topY = Math.min(
          y(d.actual ?? 0),
          d.predicted == null ? y(d.actual ?? 0) : y(d.predicted),
        );
        const svgEl = svgRef.current;
        if (!svgEl) return;
        const rect = svgEl.getBoundingClientRect();

        const tip = onTooltipRef.current;
        if (tip) {
          tip({
            visible: true,
            x: rect.left + margin.left + centerX,
            y: rect.top + margin.top + topY - 12,
            content: (
              <>
                <strong>Date:</strong> {d.date}
                <br />
                <strong>Actual:</strong> {formatMwh(d.actual)}
                <br />
                <strong>Predicted:</strong> {formatMwh(d.predicted)}
              </>
            ),
          });
        } else if (tooltip) {
          tooltip
            .style("opacity", 1)
            .html(
              `<strong>Date:</strong> ${d.date}<br/>` +
              `<strong>Actual:</strong> ${formatMwh(d.actual)}<br/>` +
              `<strong>Predicted:</strong> ${formatMwh(d.predicted)}`
            )
            .style(
              "left",
              `${rect.left + window.scrollX + margin.left + centerX + 10}px`
            )
            .style(
              "top",
              `${rect.top + window.scrollY + margin.top + topY - 30}px`
            );
        }
      };

      const showSelectionByIndex = (idx) => {
        if (!centers.length) return;
        const clamped = Math.max(0, Math.min(centers.length - 1, idx));
        keyboardIndexRef.current = clamped;
        const d = dataRef.current[clamped];
        if (!d) return;

        crosshair
          .attr("x1", centers[clamped])
          .attr("x2", centers[clamped])
          .style("opacity", 1);

        resetDots();
        actualDots.filter((_, i) => i === clamped).attr("r", DOT_R_ACTIVE);
        predictedDots.filter(dot => dot.date === d.date).attr("r", DOT_R_ACTIVE);

        showTooltipForDatum(d, centers[clamped]);
      };

      const hideSelection = () => {
        crosshair.style("opacity", 0);
        resetDots();
        const tip = onTooltipRef.current;
        if (tip) {
          tip((t) => ({ ...t, visible: false }));
        } else {
          tooltip?.style("opacity", 0);
        }
      };

      gOverlay
        .append("rect")
        .attr("width", innerWidth)
        .attr("height", innerHeight)
        .attr("fill", "transparent")
        .style("cursor", "crosshair")
        .on("mousemove", event => {
          if (!centers.length) return;
          // Any real mouse movement on the chart kicks us out of keyboard
          // navigation mode and back into mouse-follow mode.
          keyboardModeRef.current = false;
          const [mx] = d3.pointer(event);
          const idx = Math.max(
            0,
            Math.min(centers.length - 1, d3.bisectCenter(centers, mx))
          );
          showSelectionByIndex(idx);
        })
        .on("mouseleave", () => {
          // If the chart still has keyboard focus when the mouse leaves,
          // fall back to keyboard-navigation mode so the user can keep
          // arrow-keying without losing the visible selection.
          if (svgRef.current && document.activeElement === svgRef.current) {
            keyboardModeRef.current = true;
            showSelectionByIndex(keyboardIndexRef.current);
          } else {
            hideSelection();
          }
        });

      /* =========================
         Y-AXIS LABEL
         ========================= */
      g.append("text")
        .attr("transform", "rotate(-90)")
        .attr("x", -innerHeight / 2)
        .attr("y", -70)
        .attr("text-anchor", "middle")
        .style("font-size", "14px")
        .style("font-weight", "bold")
        .text("Production, MWh");

      /* =========================
         LEGEND
         ========================= */
      const legend = svg
        .append("g")
        .attr("text-anchor", "start");

      legend.append("line")
        .attr("x1", 0)
        .attr("x2", 30)
        .attr("y1", -2)
        .attr("y2", -2)
        .attr("stroke", "#4472C4")
        .attr("stroke-width", 3);

      const actualLabel = legend.append("text")
        .attr("x", 38)
        .attr("y", 2)
        .style("font-weight", "bold")
        .text("Actual (ION delivered), cumulative MWh");

      const predictedX = 38 + actualLabel.node().getComputedTextLength() + 40;

      legend.append("line")
        .attr("x1", predictedX)
        .attr("x2", predictedX + 30)
        .attr("y1", -2)
        .attr("y2", -2)
        .attr("stroke", "#ED7D31")
        .attr("stroke-width", 3);

      const predictedLabel = legend.append("text")
        .attr("x", predictedX + 38)
        .attr("y", 2)
        .style("font-weight", "bold")
        .text("Predicted — PVsyst POI (net of all losses), cumulative MWh");

      const legendWidth = predictedX + 38 + predictedLabel.node().getComputedTextLength();
      legend.attr("transform", `translate(${(width - legendWidth) / 2}, ${HEIGHT - 35})`);

      // Publish this draw cycle's selection helpers so the effect-scoped
      // keyboard/focus listeners (which outlive a single draw) can drive
      // the most recent chart instance.
      handlersRef.current = {
        centersLength: centers.length,
        showSelectionByIndex,
        hideSelection,
      };

      // If the chart was already focused when this draw ran (e.g. resize
      // fired while the user was navigating with the keyboard), re-render
      // the selection so the crosshair persists across redraws.
      if (
        keyboardModeRef.current &&
        svgRef.current &&
        document.activeElement === svgRef.current
      ) {
        showSelectionByIndex(keyboardIndexRef.current);
      }
    }

    /* =========================
      LOAD API DATA
      ========================= */
    let isMounted = true;

    const load = async () => {
      if (Array.isArray(data) && data.length > 0) {
        dataRef.current = normalizeProductionComparisonRows(data);
        draw();
        return;
      }

      if (!portfolioName) {
        dataRef.current = [];
        draw();
        return;
      }

      try {
        const payload = await apiFetch(`/api/graph/portfolio/${portfolioName}/duel-metrics/last-2-years/production/production-predicted?cumulative=1`);

        if (!isMounted) return;
        dataRef.current = normalizeProductionComparisonRows(payload);
        draw();
      } catch (error) {
        console.error("Failed to load cumulative production vs predicted:", error);
        if (!isMounted) return;
        dataRef.current = [];
        draw();
      }
    };

    load();

    const observer = new ResizeObserver(draw);
    observer.observe(containerRef.current);

    /* =========================
       KEYBOARD NAVIGATION
       Arrow-key step through x-axis points while the <svg> has focus.
       Listeners are attached once per effect run and read from
       `handlersRef` so they always drive the most recent draw cycle.
       ========================= */
    const svgEl = svgRef.current;

    const handleKeyDown = (e) => {
      const h = handlersRef.current;
      if (!keyboardModeRef.current || !h || !h.centersLength) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        if (keyboardIndexRef.current > 0) {
          h.showSelectionByIndex(keyboardIndexRef.current - 1);
        }
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        if (keyboardIndexRef.current < h.centersLength - 1) {
          h.showSelectionByIndex(keyboardIndexRef.current + 1);
        }
      }
    };

    const handleFocus = () => {
      const h = handlersRef.current;
      if (!h || !h.centersLength) return;
      keyboardModeRef.current = true;
      h.showSelectionByIndex(keyboardIndexRef.current);
    };

    const handleBlur = () => {
      keyboardModeRef.current = false;
      handlersRef.current?.hideSelection();
    };

    svgEl?.addEventListener("keydown", handleKeyDown);
    svgEl?.addEventListener("focus", handleFocus);
    svgEl?.addEventListener("blur", handleBlur);

    return () => {
      isMounted = false;
      observer.disconnect();
      tooltip?.remove?.();
      svgEl?.removeEventListener("keydown", handleKeyDown);
      svgEl?.removeEventListener("focus", handleFocus);
      svgEl?.removeEventListener("blur", handleBlur);
    };
  }, [portfolioName, data]);

  return (
    <div ref={containerRef} style={{ width: "100%", minWidth: 0 }}>
      <svg
        ref={svgRef}
        tabIndex={0}
        role="img"
        aria-label="Cumulative actuals vs predicted chart. Use left and right arrow keys to navigate between dates."
        style={{
          width: "100%",
          height: HEIGHT,
          display: "block",
        }}
      />
    </div>
  );
}
